import { lazy, Suspense } from "react";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import type { PromoSlotProps } from "./PromoSlot";

const PromoSlot = lazy(() => import("./PromoSlot"));

/**
 * The only way an ad placement should be used anywhere in the app. Two things make it safe:
 *
 *   - The module is fetched on demand (`lazy`) rather than as part of the app's initial,
 *     synchronous import graph — so a blocked or failed request for it happens well after the
 *     app has already mounted, not before.
 *   - `<ErrorBoundary>` catches that failure (including a `React.lazy` rejection, which Suspense
 *     turns into a throw during render) and renders nothing, rather than letting a third-party,
 *     often-blocked ad script take the surrounding page down with it.
 *
 * `<Suspense fallback={null}>` means there's no loading flash either — the slot is simply absent
 * until (and unless) it resolves.
 */
export function LazyPromoSlot(props: PromoSlotProps) {
  return (
    <ErrorBoundary name="promo-slot" fallback={null}>
      <Suspense fallback={null}>
        <PromoSlot {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}
